import {
  DOMParser as XMLDOMParser,
  XMLSerializer,
} from "@xmldom/xmldom";

const CHECKLIST_TITLE_PATTERN = /\b(?:Composition|Practicum) Checklist\b/i;

const localName = (element: Element) =>
  element.localName || element.tagName.split(":").pop() || element.tagName;

const getElementChildren = (element: Element) =>
  Array.from(element.childNodes).filter(
    (node): node is Element => node.nodeType === 1
  );

const getDescendantsByLocalName = (element: Element, name: string) =>
  Array.from(element.getElementsByTagName("*")).filter(
    (descendant) => localName(descendant) === name
  );

const findBody = (document: Document) =>
  Array.from(document.getElementsByTagName("*")).find(
    (element) => localName(element) === "body"
  );

const findChecklistSection = (document: Document) => {
  const body = findBody(document);
  if (!body) return undefined;

  const bodyChildren = getElementChildren(body);
  const firstSectionBreakIndex = bodyChildren.findIndex(
    (element) =>
      localName(element) === "p" &&
      getDescendantsByLocalName(element, "sectPr").length > 0
  );

  if (firstSectionBreakIndex <= 0) return undefined;

  const firstSectionChildren = bodyChildren.slice(0, firstSectionBreakIndex);
  const tableIndexes = firstSectionChildren.flatMap((element, index) =>
    localName(element) === "tbl" ? [index] : []
  );

  if (
    tableIndexes.length !== 1 ||
    tableIndexes[0] !== firstSectionChildren.length - 1
  ) {
    return undefined;
  }

  const headingText = firstSectionChildren
    .slice(0, tableIndexes[0])
    .filter((element) => localName(element) === "p")
    .map((element) =>
      getDescendantsByLocalName(element, "t")
        .map((textElement) => textElement.textContent ?? "")
        .join("")
        .replace(/\s+/g, " ")
        .trim()
    )
    .filter(Boolean);

  if (!headingText.some((line) => CHECKLIST_TITLE_PATTERN.test(line))) {
    return undefined;
  }

  const remainingContent = bodyChildren
    .slice(firstSectionBreakIndex + 1)
    .some((element) => localName(element) !== "sectPr");

  if (!remainingContent) return undefined;

  return {
    body,
    sectionChildren: bodyChildren.slice(0, firstSectionBreakIndex + 1),
  };
};

const parseWordDocumentXml = (documentXml: string) =>
  new XMLDOMParser().parseFromString(documentXml, "text/xml");

export const hasWritingCompanionChecklistSection = (documentXml: string) =>
  findChecklistSection(parseWordDocumentXml(documentXml)) != undefined;

export const stripWritingCompanionChecklistSection = (documentXml: string) => {
  const document = parseWordDocumentXml(documentXml);
  const checklistSection = findChecklistSection(document);

  if (!checklistSection) {
    return { documentXml, stripped: false };
  }

  checklistSection.sectionChildren.forEach((element) =>
    checklistSection.body.removeChild(element)
  );

  return {
    documentXml: new XMLSerializer().serializeToString(document),
    stripped: true,
  };
};

